import { rpx } from 'client/lib/rpx-client';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { LoadingIndicator } from '@components/loading-indicator';
import { showError } from '@components/app-error';
import { MemberDetailPanel } from './member-detail-panel';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

type SavedList = Awaited<ReturnType<typeof rpx.savedLists.list>>['lists'][number];
type Member = Awaited<ReturnType<typeof rpx.savedLists.evaluate>>['members'][number];
type EntryChoice = Awaited<ReturnType<typeof rpx.savedLists.entryCourseChoices>>['choices'][number];
type OutreachChoices = Awaited<ReturnType<typeof rpx.savedLists.outreachChoices>>;
type Definition = SavedList['definition'];

const AIP_STATUS_OPTIONS: Array<{ value: 'current' | 'canceled' | 'paused'; label: string }> = [
  { value: 'current', label: 'Current member' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'paused', label: 'Paused' },
];

const SOURCE_OPTIONS: Array<{ value: 'paid' | 'invited' | 'orphan'; label: string }> = [
  { value: 'paid', label: 'Paid' },
  { value: 'invited', label: 'Invited (bulk)' },
  { value: 'orphan', label: 'No purchase record' },
];

const CANCELLATION_OPTIONS: Array<{
  value: 'active' | 'canceling' | 'canceled' | 'paused';
  label: string;
}> = [
  { value: 'active', label: 'Active' },
  { value: 'canceling', label: 'Canceling' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'paused', label: 'Paused' },
];

const BAND_OPTIONS: Array<{
  value:
    | 'newAndFindingFeet'
    | 'cameOnceAndDrifted'
    | 'slippingAway'
    | 'hotCancellations'
    | 'warmSlipping'
    | 'watchTrialEnding'
    | 'champions'
    | 'asyncEngaged';
  label: string;
}> = [
  { value: 'newAndFindingFeet', label: 'New & finding feet' },
  { value: 'cameOnceAndDrifted', label: 'Came once & drifted' },
  { value: 'slippingAway', label: 'Slipping away' },
  { value: 'hotCancellations', label: 'HOT — cancellations' },
  { value: 'warmSlipping', label: 'WARM — slipping' },
  { value: 'watchTrialEnding', label: 'WATCH — trial ending' },
  { value: 'champions', label: 'Champions' },
  { value: 'asyncEngaged', label: 'Async engaged' },
];

const BILLING_LABELS: Record<Member['billingStatus'], string> = {
  trial: 'On trial',
  paying: 'Paying',
  stopped: 'Canceled or paused',
  billedElsewhere: 'Not billed here',
};

const BILLING_OPTIONS = (Object.keys(BILLING_LABELS) as Member['billingStatus'][]).map((value) => ({
  value,
  label: BILLING_LABELS[value],
}));

const FIRST_WEEKS_OPTIONS: Array<{ value: 'oneCall' | 'noCallsNoCourses'; label: string }> = [
  { value: 'oneCall', label: 'Exactly 1 call' },
  { value: 'noCallsNoCourses', label: 'No calls and no courses' },
];

const EMPTY_DEF: Definition = {};
const EMPTY_OUTREACH: OutreachChoices = { messages: [], courses: [] };

type CancellationStatus = 'active' | 'canceling' | 'canceled' | 'paused';
const VALID_CANCELLATION: CancellationStatus[] = ['active', 'canceling', 'canceled', 'paused'];

// Build a filter definition from URL params, for deep-links into a pre-filtered
// list (e.g. the monthly cohort retention email's "view who left" link).
function readDefFromUrl(): Definition | undefined {
  const params = new URLSearchParams(window.location.search);
  const entryCourse = params.get('entryCourse');
  const status = params.get('status');
  if (!entryCourse && !status) {
    return undefined;
  }
  const def: Definition = {};
  if (entryCourse) {
    def.entryCourseIds = [entryCourse as UUID];
  }
  if (status) {
    const values = status
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is CancellationStatus =>
        VALID_CANCELLATION.includes(s as CancellationStatus),
      );
    if (values.length > 0) {
      def.cancellationStatus = values;
    }
  }
  return def;
}

export function ListsTab() {
  const [lists, setLists] = useState<SavedList[]>([]);
  const [entryChoices, setEntryChoices] = useState<EntryChoice[]>([]);
  const [outreachChoices, setOutreachChoices] = useState<OutreachChoices>(EMPTY_OUTREACH);
  const [activeList, setActiveList] = useState<SavedList | undefined>(undefined);
  const [draftDef, setDraftDef] = useState<Definition>(EMPTY_DEF);
  const [draftName, setDraftName] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [openMemberId, setOpenMemberId] = useState<UUID | undefined>(undefined);
  const [showStaffTest, setShowStaffTest] = useState(false);

  const staffTestCount = useMemo(
    () => members.filter((m) => m.kind !== 'member').length,
    [members],
  );

  async function refreshLists() {
    try {
      const [l, e, o] = await Promise.all([
        rpx.savedLists.list(),
        rpx.savedLists.entryCourseChoices(),
        rpx.savedLists.outreachChoices(),
      ]);
      setLists(l.lists);
      setEntryChoices(e.choices);
      setOutreachChoices(o);
    } catch (err) {
      showError(err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    refreshLists();
    // Seed filters from the URL so deep-links (e.g. from the monthly cohort
    // retention email) land pre-filtered: ?tab=lists&entryCourse=<id>&status=canceled,canceling
    const seeded = readDefFromUrl();
    if (seeded) {
      setDraftDef(seeded);
    }
    runEvaluation(seeded ?? EMPTY_DEF, showStaffTest);
  }, []);

  async function runEvaluation(def: Definition, includeStaffTest: boolean) {
    setIsEvaluating(true);
    try {
      const result = await rpx.savedLists.evaluate({
        definition: def,
        includeStaffTest: includeStaffTest || undefined,
      });
      setMembers(result.members);
    } catch (err) {
      showError(err);
    } finally {
      setIsEvaluating(false);
    }
  }

  function toggleShowStaffTest(next: boolean) {
    setShowStaffTest(next);
    runEvaluation(draftDef, next);
  }

  function startNew() {
    setActiveList(undefined);
    setDraftDef(EMPTY_DEF);
    setDraftName('');
    setMembers([]);
  }

  function openList(list: SavedList) {
    setActiveList(list);
    setDraftDef(list.definition);
    setDraftName(list.name);
    runEvaluation(list.definition, showStaffTest);
  }

  async function saveList() {
    const name = draftName.trim();
    if (!name) {
      showError(new Error('Name your list before saving.'));
      return;
    }
    setIsSaving(true);
    try {
      if (activeList) {
        await rpx.savedLists.update({
          listId: activeList.id,
          name,
          definition: draftDef,
        });
      } else {
        await rpx.savedLists.create({ name, definition: draftDef });
      }
      await refreshLists();
    } catch (err) {
      showError(err);
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteList(list: SavedList) {
    if (!confirm(`Delete the list "${list.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await rpx.savedLists.remove({ listId: list.id });
      if (activeList?.id === list.id) {
        startNew();
      }
      await refreshLists();
    } catch (err) {
      showError(err);
    }
  }

  function downloadCsv() {
    if (members.length === 0) {
      return;
    }
    const csv = membersToCsv(members);
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = (draftName.trim() || 'list').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();
    a.download = `${safeName}-${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const isDirty = useMemo(() => {
    if (!activeList) {
      return draftName.trim().length > 0 || Object.keys(draftDef).length > 0;
    }
    return (
      draftName !== activeList.name ||
      JSON.stringify(draftDef) !== JSON.stringify(activeList.definition)
    );
  }, [activeList, draftDef, draftName]);

  if (isLoading) {
    return (
      <div class="p-8">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <>
      <p class="-mt-2 mb-6 text-sm text-gray-500">
        Compose ad-hoc lists of AIP members. Filters combine with AND; values within a filter
        combine with OR (e.g. "AIS or EAI"). Save the queries you reuse. Export to CSV.
      </p>
      <div class="flex gap-6">
        <aside class="w-60 shrink-0 border-r border-gray-200 pr-4">
          <div class="mb-3 flex items-center justify-between">
            <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500">Saved lists</h3>
            <button
              type="button"
              class="text-xs text-indigo-700 hover:underline"
              onClick={startNew}
            >
              + New
            </button>
          </div>
          {lists.length === 0 ? (
            <p class="text-sm text-gray-500">No saved lists yet.</p>
          ) : (
            <ul class="flex flex-col gap-1">
              {lists.map((l) => (
                <li key={l.id}>
                  <button
                    type="button"
                    class={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm hover:bg-gray-100 ${
                      activeList?.id === l.id ? 'bg-indigo-50 font-medium text-indigo-900' : ''
                    }`}
                    onClick={() => openList(l)}
                  >
                    <span class="truncate">{l.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <main class="flex grow flex-col gap-4">
          <div class="flex items-center gap-3">
            <input
              type="text"
              class="grow rounded border border-gray-300 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none"
              placeholder="Name this list…"
              value={draftName}
              onInput={(e) => setDraftName((e.target as HTMLInputElement).value)}
            />
            <button
              type="button"
              class="rounded bg-indigo-600 px-3 py-1 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              onClick={saveList}
              disabled={isSaving || !draftName.trim() || (!isDirty && !!activeList)}
            >
              {isSaving ? 'Saving…' : activeList ? 'Save changes' : 'Save list'}
            </button>
            {activeList && (
              <button
                type="button"
                class="text-sm text-red-700 hover:underline"
                onClick={() => deleteList(activeList)}
              >
                Delete
              </button>
            )}
          </div>

          <FilterForm
            definition={draftDef}
            entryChoices={entryChoices}
            outreachChoices={outreachChoices}
            onChange={setDraftDef}
          />

          <div class="flex items-center justify-between border-t border-gray-200 pt-4">
            <div class="flex items-center gap-4 text-sm text-gray-700">
              <span>
                {isEvaluating
                  ? 'Evaluating…'
                  : members.length === 0
                    ? '0 members match.'
                    : `${members.length} ${members.length === 1 ? 'member' : 'members'} match — ${statusBreakdown(members)}.`}
              </span>
              <label class="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={showStaffTest}
                  onChange={(e) => toggleShowStaffTest((e.target as HTMLInputElement).checked)}
                />
                Show staff &amp; test
                {showStaffTest && staffTestCount > 0 && (
                  <span class="text-xs text-gray-500">({staffTestCount} included)</span>
                )}
              </label>
            </div>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="rounded border border-indigo-600 px-3 py-1 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                onClick={() => runEvaluation(draftDef, showStaffTest)}
                disabled={isEvaluating}
              >
                Run query
              </button>
              <button
                type="button"
                class="rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                onClick={downloadCsv}
                disabled={members.length === 0}
              >
                Export CSV
              </button>
            </div>
          </div>

          <ResultsTable members={members} onOpen={(id) => setOpenMemberId(id)} />
        </main>
      </div>
      {openMemberId && (
        <MemberDetailPanel
          memberUserId={openMemberId}
          membershipScope="all"
          onClose={() => setOpenMemberId(undefined)}
        />
      )}
    </>
  );
}

function FilterForm(props: {
  definition: Definition;
  entryChoices: EntryChoice[];
  outreachChoices: OutreachChoices;
  onChange: (def: Definition) => void;
}) {
  const { definition, onChange } = props;

  function patch(p: Partial<Definition>) {
    const next: Definition = { ...definition, ...p };
    for (const key of Object.keys(next) as (keyof Definition)[]) {
      const v = next[key];
      if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) {
        delete next[key];
      }
    }
    onChange(next);
  }

  return (
    <div class="grid grid-cols-1 gap-4 rounded border border-gray-200 bg-gray-50 p-4 md:grid-cols-2">
      <FilterField label="AIP status">
        <MultiSelect
          options={AIP_STATUS_OPTIONS}
          value={definition.aipStatus ?? []}
          onChange={(v) => patch({ aipStatus: v as Definition['aipStatus'] })}
        />
      </FilterField>
      <FilterField label="Entry course (came in via)">
        <MultiSelect
          options={props.entryChoices.map((c) => ({ value: c.courseId, label: c.title }))}
          value={definition.entryCourseIds ?? []}
          onChange={(v) => patch({ entryCourseIds: v as UUID[] })}
        />
      </FilterField>
      <FilterField label="Payment type">
        <MultiSelect
          options={SOURCE_OPTIONS}
          value={definition.membershipSource ?? []}
          onChange={(v) => patch({ membershipSource: v as Definition['membershipSource'] })}
        />
      </FilterField>
      <FilterField label="Subscription status">
        <MultiSelect
          options={CANCELLATION_OPTIONS}
          value={definition.cancellationStatus ?? []}
          onChange={(v) => patch({ cancellationStatus: v as Definition['cancellationStatus'] })}
        />
      </FilterField>
      <FilterField label="Joined (date range)" hint="Dates evaluated in UTC.">
        <div class="flex items-center gap-2">
          <input
            type="date"
            class="grow rounded border border-gray-300 px-2 py-1 text-sm"
            aria-label="Joined from"
            value={definition.joinedAfter ?? ''}
            onInput={(e) => patch({ joinedAfter: (e.target as HTMLInputElement).value })}
          />
          <span class="text-xs text-gray-500">→</span>
          <input
            type="date"
            class="grow rounded border border-gray-300 px-2 py-1 text-sm"
            aria-label="Joined to"
            value={definition.joinedBefore ?? ''}
            onInput={(e) => patch({ joinedBefore: (e.target as HTMLInputElement).value })}
          />
        </div>
      </FilterField>
      <FilterField label="Cancelled (date range)" hint="Dates evaluated in UTC.">
        <div class="flex items-center gap-2">
          <input
            type="date"
            class="grow rounded border border-gray-300 px-2 py-1 text-sm"
            aria-label="Cancelled from"
            value={definition.cancelledAfter ?? ''}
            onInput={(e) => patch({ cancelledAfter: (e.target as HTMLInputElement).value })}
          />
          <span class="text-xs text-gray-500">→</span>
          <input
            type="date"
            class="grow rounded border border-gray-300 px-2 py-1 text-sm"
            aria-label="Cancelled to"
            value={definition.cancelledBefore ?? ''}
            onInput={(e) => patch({ cancelledBefore: (e.target as HTMLInputElement).value })}
          />
        </div>
      </FilterField>
      <FilterField label="Retention band">
        <MultiSelect
          options={BAND_OPTIONS}
          value={definition.retentionBands ?? []}
          onChange={(v) => patch({ retentionBands: v as Definition['retentionBands'] })}
        />
      </FilterField>
      <FilterField label="Billing">
        <MultiSelect
          options={BILLING_OPTIONS}
          value={definition.billingStatus ?? []}
          onChange={(v) => patch({ billingStatus: v as Definition['billingStatus'] })}
        />
      </FilterField>
      <FilterField
        label="First 3 weeks of membership"
        hint="Counts AI Playground calls and courses joined, from the start of the latest free trial, or from joining if there was none. Only members past their first 3 weeks."
      >
        <MultiSelect
          options={FIRST_WEEKS_OPTIONS}
          value={definition.firstThreeWeeks ? [definition.firstThreeWeeks] : []}
          onChange={(v) =>
            patch({
              firstThreeWeeks: v.find((x) => x !== definition.firstThreeWeeks),
            })
          }
        />
      </FilterField>
      <FilterField
        label="Clicked a link in"
        hint="Emails sent in the last 120 days. Counts a click on any link in the email."
      >
        <MessagePicker
          messages={props.outreachChoices.messages}
          value={definition.clickedMessageIds ?? []}
          onChange={(v) => patch({ clickedMessageIds: v })}
        />
      </FilterField>
      <FilterField label="Not signed up for">
        <select
          aria-label="Not signed up for"
          class="rounded border border-gray-300 bg-white px-2 py-1 text-sm"
          value={definition.notEnrolledCourseId ?? ''}
          onChange={(e) =>
            patch({ notEnrolledCourseId: (e.target as HTMLSelectElement).value || undefined })
          }
        >
          <option value="">Any course</option>
          {definition.notEnrolledCourseId &&
            !props.outreachChoices.courses.some(
              (c) => c.courseId === definition.notEnrolledCourseId,
            ) && <option value={definition.notEnrolledCourseId}>Course no longer listed</option>}
          {props.outreachChoices.courses.map((c) => (
            <option key={c.courseId} value={c.courseId}>
              {c.title}
            </option>
          ))}
        </select>
      </FilterField>
    </div>
  );
}

function MessagePicker(props: {
  messages: OutreachChoices['messages'];
  value: UUID[];
  onChange: (next: UUID[]) => void;
}) {
  const listedIds = new Set(props.messages.map((m) => m.messageId));
  const unlistedIds = props.value.filter((id) => !listedIds.has(id));
  if (props.messages.length === 0 && unlistedIds.length === 0) {
    return <span class="text-xs text-gray-500">No emails sent in the last 120 days.</span>;
  }
  function toggle(id: UUID, checked: boolean) {
    props.onChange(checked ? [...props.value, id] : props.value.filter((x) => x !== id));
  }
  return (
    <div class="flex max-h-48 flex-col gap-1 overflow-y-auto rounded border border-gray-300 bg-white p-2">
      {unlistedIds.map((id) => (
        <label key={id} class="flex items-start gap-2 text-xs text-gray-700">
          <input
            type="checkbox"
            class="mt-0.5"
            checked
            onChange={(e) => toggle(id, (e.target as HTMLInputElement).checked)}
          />
          <span>Older email, no longer listed</span>
        </label>
      ))}
      {props.messages.map((m) => (
        <label key={m.messageId} class="flex items-start gap-2 text-xs text-gray-700">
          <input
            type="checkbox"
            class="mt-0.5"
            checked={props.value.includes(m.messageId)}
            onChange={(e) => toggle(m.messageId, (e.target as HTMLInputElement).checked)}
          />
          <span class="min-w-0">
            <span class="block truncate" title={m.title}>
              {m.title}
            </span>
            <span class="text-gray-500">
              {dayjs(m.firstSentAt).format('MMM D')} · {m.clickedCount} of {m.sentCount} clicked
            </span>
          </span>
        </label>
      ))}
    </div>
  );
}

function FilterField(props: { label: string; hint?: string; children: preact.ComponentChildren }) {
  return (
    <div class="flex flex-col gap-1">
      <span class="text-xs font-medium text-gray-700">{props.label}</span>
      {props.children}
      {props.hint && <span class="text-xs text-gray-500">{props.hint}</span>}
    </div>
  );
}

function MultiSelect<T extends string>(props: {
  options: Array<{ value: T; label: string }>;
  value: T[];
  onChange: (next: T[]) => void;
}) {
  function toggle(v: T) {
    if (props.value.includes(v)) {
      props.onChange(props.value.filter((x) => x !== v));
    } else {
      props.onChange([...props.value, v]);
    }
  }
  return (
    <div class="flex flex-wrap gap-1">
      {props.options.map((opt) => {
        const selected = props.value.includes(opt.value);
        return (
          <button
            type="button"
            key={opt.value}
            class={`rounded-full border px-2 py-0.5 text-xs ${
              selected
                ? 'border-indigo-600 bg-indigo-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-400'
            }`}
            onClick={() => toggle(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function statusBreakdown(members: Member[]): string {
  let current = 0;
  let canceled = 0;
  let paused = 0;
  for (const m of members) {
    if (m.cancellationStatus === 'paused') {
      paused += 1;
    } else if (m.cancellationStatus === 'canceled' || !m.isEnabled) {
      canceled += 1;
    } else {
      current += 1;
    }
  }
  const parts: string[] = [];
  if (current > 0) {
    parts.push(`${current} current`);
  }
  if (canceled > 0) {
    parts.push(`${canceled} canceled`);
  }
  if (paused > 0) {
    parts.push(`${paused} paused`);
  }
  return parts.join(', ');
}

function ResultsTable(props: { members: Member[]; onOpen: (userId: UUID) => void }) {
  if (props.members.length === 0) {
    return (
      <p class="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
        No members match. Either no one fits the criteria, or you haven't run the query yet — click
        Run query above.
      </p>
    );
  }
  return (
    <div class="overflow-auto rounded border border-gray-200">
      <table class="w-full text-sm">
        <thead class="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th class="whitespace-nowrap px-3 py-2 text-left">Name</th>
            <th class="whitespace-nowrap px-3 py-2 text-left">Email</th>
            <th class="whitespace-nowrap px-3 py-2 text-left">Entry course</th>
            <th class="whitespace-nowrap px-3 py-2 text-left">Joined</th>
            <th class="whitespace-nowrap px-3 py-2 text-left">Status</th>
            <th class="whitespace-nowrap px-3 py-2 text-left">Billing</th>
          </tr>
        </thead>
        <tbody>
          {props.members.map((m) => (
            <tr
              key={m.userId}
              class="cursor-pointer border-t border-gray-100 hover:bg-indigo-50"
              onClick={() => props.onOpen(m.userId)}
            >
              <td class="max-w-[18ch] truncate px-3 py-2" title={m.name || ''}>
                {m.name || '—'}
              </td>
              <td class="max-w-[24ch] truncate px-3 py-2 text-gray-700" title={m.email}>
                {m.email}
              </td>
              <td
                class="max-w-[28ch] truncate px-3 py-2 text-gray-700"
                title={m.entryCourseTitle || ''}
              >
                {m.entryCourseTitle || '—'}
              </td>
              <td class="whitespace-nowrap px-3 py-2 text-gray-700">
                {dayjs(m.membershipCreatedAt).format('MMM D, YYYY')}
              </td>
              <td class="whitespace-nowrap px-3 py-2 text-gray-700">{m.cancellationStatus}</td>
              <td class="whitespace-nowrap px-3 py-2 text-gray-700">
                {BILLING_LABELS[m.billingStatus]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function membersToCsv(members: Member[]): string {
  const cols = [
    'name',
    'email',
    'entry_course',
    'membership_source',
    'cancellation_status',
    'billing',
    'joined_date',
    'cancelled_date',
    'calls_attended',
    'first_3_weeks_start',
    'calls_first_3_weeks',
    'courses_joined_first_3_weeks',
    'posts_count',
    'mrr_dollars',
  ];
  const header = cols.join(',');
  const rows = members.map((m) => {
    const cells = [
      m.name,
      m.email,
      m.entryCourseTitle,
      m.membershipSource,
      m.cancellationStatus,
      BILLING_LABELS[m.billingStatus],
      m.membershipCreatedAt ? dayjs.utc(m.membershipCreatedAt).format('YYYY-MM-DD') : '',
      m.cancelledAt ? dayjs(m.cancelledAt).format('YYYY-MM-DD') : '',
      String(m.callsAttended),
      m.firstThreeWeeksStartedAt ? dayjs.utc(m.firstThreeWeeksStartedAt).format('YYYY-MM-DD') : '',
      m.callsFirstThreeWeeks,
      m.coursesJoinedFirstThreeWeeks,
      String(m.postsCount),
      m.mrrCents !== undefined ? (m.mrrCents / 100).toFixed(2) : '',
    ];
    return cells.map(csvCell).join(',');
  });
  return [header, ...rows].join('\n');
}

function csvCell(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }
  let s = String(value);
  if (/^[=+\-@\t\r]/.test(s)) {
    s = `'${s}`;
  }
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
